import type { RoleDefinition, ATSResult } from '../types/api';

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been',
  'has', 'have', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
  'should', 'may', 'might', 'can', 'shall', 'that', 'this', 'these',
  'those', 'it', 'its', 'not', 'no', 'as', 'if', 'than', 'so', 'up',
  'required', 'preferred', 'plus', 'minimum', 'within', 'years', 'year',
  'experience', 'ability', 'strong', 'proven', 'track', 'record',
]);

/**
 * Extract meaningful keyword phrases from text.
 * Pulls out multi-word terms first, then single significant words.
 */
function extractKeywordPhrases(texts: string[]): string[] {
  const allText = texts.join(' ').toLowerCase();
  const phrases: string[] = [];

  // Extract multi-word phrases (2-3 word combos that appear meaningful)
  const multiWord = allText.match(
    /\b(?:contact center|workforce management|six sigma|black belt|digital transformation|cost reduction|vendor management|data[- ]driven|real[- ]time|continuous improvement|machine learning|fighter pilot|hog farmer|mig kills?|f-14|artificial intelligence|customer experience|employee experience|process improvement|change management|project management|supply chain|business transformation|operations research|predictive analytics|forecasting|scheduling|p&l|budget management)\b/g,
  );
  if (multiWord) phrases.push(...new Set(multiWord));

  // Extract single meaningful keywords
  const words = allText
    .replace(/[^a-z0-9\s\-\/\+]/g, '')
    .split(/\s+/)
    .filter((w) => w.length > 2 && !STOP_WORDS.has(w));

  // Deduplicate and take unique single words not already covered by phrases
  const phraseWords = new Set(phrases.join(' ').split(/\s+/));
  for (const w of words) {
    if (!phraseWords.has(w) && !phrases.includes(w)) {
      phrases.push(w);
    }
  }

  return [...new Set(phrases)];
}

/**
 * Simplistic ATS keyword scanner.
 * Pulls keywords from ALL role definition fields, then does pure string matching
 * against the candidate's API data. No intelligence, no context — just keywords.
 *
 * Note: ATS is intentionally simplistic and does NOT use scoring weights.
 * That's the whole point — ATS can't weigh context, only the Intelligence Engine can.
 */
export function simulateATS(
  role: RoleDefinition,
  candidateText: string,
): ATSResult {
  // Gather ALL text from the role definition
  const roleTexts = [
    role.title,
    ...role.qualifications,
    ...role.outcomes,
    role.companyType,
    role.yearsExperience ?? '',
    role.certifications ?? '',
    role.jobClassification ?? '',
    role.education ?? '',
  ];

  // Extract keywords from ALL role fields
  const keywords = extractKeywordPhrases(roleTexts);
  const candidateLower = candidateText.toLowerCase();

  // Pure keyword matching — does the candidate text contain each keyword?
  const keywordDetails = keywords.slice(0, 25).map((kw) => ({
    keyword: kw,
    found: candidateLower.includes(kw),
  }));

  const matched = keywordDetails.filter((d) => d.found).length;
  const total = keywordDetails.length;

  // ATS checks derived from form fields (simplistic pass/fail)
  const checks: ATSResult['checks'] = [];

  if (role.yearsExperience) {
    const hasYearsKeyword = candidateLower.includes('year') || candidateLower.includes('experience');
    checks.push({
      label: 'Years Experience',
      status: hasYearsKeyword ? 'pass' : 'flag',
      value: hasYearsKeyword ? `Detected (req: ${role.yearsExperience})` : `${role.yearsExperience} required — not verified`,
    });
  }

  if (role.education) {
    const hasDegree = candidateLower.includes('bachelor') || candidateLower.includes('master') ||
      candidateLower.includes('mba') || candidateLower.includes('degree') || candidateLower.includes('bs ') || candidateLower.includes('ms ');
    checks.push({
      label: 'Education',
      status: hasDegree ? 'pass' : 'flag',
      value: hasDegree ? 'Degree keywords detected' : `${role.education} — not found`,
    });
  }

  if (role.certifications) {
    const certKeywords = role.certifications.toLowerCase().split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
    const foundCerts = certKeywords.filter((c) => candidateLower.includes(c));
    checks.push({
      label: 'Certifications',
      status: foundCerts.length > 0 ? 'pass' : 'fail',
      value: foundCerts.length > 0 ? `Found: ${foundCerts.join(', ')}` : `Required: ${role.certifications} — none matched`,
    });
  }

  checks.push({
    label: 'Work Classification',
    status: 'pass',
    value: role.jobClassification === 'remote' ? 'Remote eligible' : role.jobClassification === 'hybrid' ? 'Hybrid eligible' : 'On-site',
  });

  checks.push({
    label: 'Resume Format',
    status: 'pass',
    value: 'Parsed successfully',
  });

  // Score: purely keyword match percentage (simplistic)
  const keywordScore = total > 0 ? (matched / total) * 100 : 50;
  const checkPassRate = checks.length > 0
    ? (checks.filter((c) => c.status === 'pass').length / checks.length) * 100
    : 50;
  const overall = Math.round(keywordScore * 0.7 + checkPassRate * 0.3);

  let disposition = 'Qualified — placed in review queue';
  if (overall >= 80) disposition = 'Strong keyword match — forwarded to recruiter';
  else if (overall < 40) disposition = 'Below threshold — auto-rejected';

  return {
    overall_score: overall,
    keywords_matched: matched,
    keywords_total: total,
    keyword_details: keywordDetails,
    checks,
    disposition,
  };
}
